import { Component } from '@theme/component';

export class ShoppableCarouselComponent extends Component {
  requiredRefs = ['track'];

  #activeIndex = 0;
  #rafId = null;
  #resizeObserver = null;
  #realSlides = [];
  #totalClones = 0;
  #isJumping = false;
  #scrollEndTimer = null;

  connectedCallback() {
    super.connectedCallback();
    const { track } = this.refs;
    if (!track) return;

    this.#realSlides = [...this.refs.slides || []];
    if (this.#realSlides.length > 1) {
      this.#createClones();
    }

    track.addEventListener('click', this.#onSlideClick);
    track.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#resizeObserver = new ResizeObserver(this.#onResize);
    this.#resizeObserver.observe(track);

    // Double RAF: wait for images to size the slides before measuring
    requestAnimationFrame(() => requestAnimationFrame(() => {
      this.#updateTrackPadding();
      this.#scrollToRealIndex(0, 'instant');
    }));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const { track } = this.refs;
    if (track) {
      track.removeEventListener('click', this.#onSlideClick);
      track.removeEventListener('scroll', this.#onScroll);
    }
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    if (this.#rafId) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
    if (this.#scrollEndTimer) {
      clearTimeout(this.#scrollEndTimer);
      this.#scrollEndTimer = null;
    }
  }

  /**
   * Clone all real slides and prepend/append them to create the
   * illusion of infinite scrolling.
   */
  #createClones() {
    const { track } = this.refs;
    const count = this.#realSlides.length;
    this.#totalClones = count;

    // Append clones after
    for (let i = 0; i < count; i++) {
      const clone = this.#realSlides[i].cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.classList.add('shoppable-carousel__slide--clone');
      clone.removeAttribute('ref');
      track.appendChild(clone);
    }

    // Prepend clones before
    for (let i = count - 1; i >= 0; i--) {
      const clone = this.#realSlides[i].cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.classList.add('shoppable-carousel__slide--clone');
      clone.removeAttribute('ref');
      track.prepend(clone);
    }
  }

  /**
   * Get all slide elements (clones + real) in DOM order.
   */
  get #allSlides() {
    return [...this.refs.track.querySelectorAll('.shoppable-carousel__slide')];
  }

  /**
   * The offset into #allSlides where the real slides begin.
   */
  get #realOffset() {
    return this.#totalClones;
  }

  /**
   * Compute and set track padding-inline so the first and last visible slides
   * can scroll to center.
   */
  #updateTrackPadding() {
    const { track } = this.refs;
    const allSlides = this.#allSlides;
    if (!allSlides.length || !track) return;

    track.style.paddingInlineStart = '0';
    track.style.paddingInlineEnd = '0';

    const halfTrack = track.clientWidth / 2;
    const paddingStart = Math.max(0, halfTrack - allSlides[0].offsetWidth / 2);
    const paddingEnd = Math.max(0, halfTrack - allSlides[allSlides.length - 1].offsetWidth / 2);

    track.style.paddingInlineStart = `${paddingStart}px`;
    track.style.paddingInlineEnd = `${paddingEnd}px`;
  }

  #onSlideClick = (event) => {
    const slide = event.target.closest('.shoppable-carousel__slide');
    if (!slide) return;

    // Don't intercept clicks on the already-active slide (allow hotspot links)
    if (slide.getAttribute('data-active') === 'true') return;

    event.preventDefault();
    const allSlides = this.#allSlides;
    const domIndex = allSlides.indexOf(slide);
    if (domIndex === -1) return;

    const { track } = this.refs;
    const targetScrollLeft = slide.offsetLeft + slide.offsetWidth / 2 - track.clientWidth / 2;
    track.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
  };

  #onScroll = () => {
    if (this.#isJumping) return;
    if (this.#rafId) return;
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#findCenteredSlide();
    });

    // Detect scroll end to reposition if in clone territory
    if (this.#scrollEndTimer) clearTimeout(this.#scrollEndTimer);
    this.#scrollEndTimer = setTimeout(() => {
      this.#scrollEndTimer = null;
      this.#snapToRealIfNeeded();
    }, 100);
  };

  #onResize = () => {
    this.#updateTrackPadding();
    this.#scrollToRealIndex(this.#activeIndex, 'instant');
  };

  #findCenteredSlide() {
    const { track } = this.refs;
    const allSlides = this.#allSlides;
    if (!allSlides.length || !track) return;

    const trackCenter = track.scrollLeft + track.clientWidth / 2;
    let closestIndex = 0;
    let closestDist = Infinity;

    for (let i = 0; i < allSlides.length; i++) {
      const slideCenter = allSlides[i].offsetLeft + allSlides[i].offsetWidth / 2;
      const dist = Math.abs(slideCenter - trackCenter);
      if (dist < closestDist) {
        closestDist = dist;
        closestIndex = i;
      }
    }

    // Map to real index
    const realCount = this.#realSlides.length;
    const realIndex = realCount > 1
      ? ((closestIndex - this.#realOffset) % realCount + realCount) % realCount
      : closestIndex;

    this.#setActive(realIndex, allSlides);
  }

  #setActive(realIndex, allSlides) {
    if (!allSlides) allSlides = this.#allSlides;
    const realCount = this.#realSlides.length;

    for (let i = 0; i < allSlides.length; i++) {
      const mapped = realCount > 1
        ? ((i - this.#realOffset) % realCount + realCount) % realCount
        : i;
      allSlides[i].setAttribute('data-active', String(mapped === realIndex));
    }
    this.#activeIndex = realIndex;
  }

  /**
   * If scroll has stopped on a clone, silently jump to the matching real slide.
   */
  #snapToRealIfNeeded() {
    if (this.#realSlides.length <= 1) return;
    const { track } = this.refs;
    const allSlides = this.#allSlides;
    const trackCenter = track.scrollLeft + track.clientWidth / 2;

    // Find which DOM slide is centered
    let closestIdx = 0;
    let closestDist = Infinity;
    for (let i = 0; i < allSlides.length; i++) {
      const center = allSlides[i].offsetLeft + allSlides[i].offsetWidth / 2;
      const dist = Math.abs(center - trackCenter);
      if (dist < closestDist) {
        closestDist = dist;
        closestIdx = i;
      }
    }

    const realStart = this.#realOffset;
    const realEnd = realStart + this.#realSlides.length - 1;

    // If centered on a real slide, no jump needed
    if (closestIdx >= realStart && closestIdx <= realEnd) return;

    // Map to real index and jump
    const realCount = this.#realSlides.length;
    const realIndex = ((closestIdx - realStart) % realCount + realCount) % realCount;
    this.#scrollToRealIndex(realIndex, 'instant');
  }

  /**
   * Scroll to a real slide by its index (0-based within original slides).
   */
  #scrollToRealIndex(index, behavior = 'smooth') {
    const { track } = this.refs;
    const allSlides = this.#allSlides;
    if (!track || !allSlides.length) return;

    const domIndex = this.#realOffset + index;
    const slide = allSlides[domIndex];
    if (!slide) return;

    const targetScrollLeft = slide.offsetLeft + slide.offsetWidth / 2 - track.clientWidth / 2;

    if (behavior === 'instant') {
      this.#isJumping = true;
      track.style.scrollBehavior = 'auto';
      track.scrollLeft = targetScrollLeft;
      track.style.scrollBehavior = '';
      this.#setActive(index, allSlides);
      // Use RAF to re-enable scroll handling after the jump
      requestAnimationFrame(() => {
        this.#isJumping = false;
      });
    } else {
      track.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
    }
  }

  scrollPrev = (event) => {
    event?.preventDefault();
    const realCount = this.#realSlides.length;
    if (realCount <= 1) return;

    // Instead of clamping, scroll to the clone position for wrapping
    const { track } = this.refs;
    const allSlides = this.#allSlides;
    const currentDomCenter = this.#realOffset + this.#activeIndex;

    // Scroll to the previous DOM slide (could be a clone)
    const prevDomIndex = currentDomCenter - 1;
    if (prevDomIndex < 0) return;

    const slide = allSlides[prevDomIndex];
    const targetScrollLeft = slide.offsetLeft + slide.offsetWidth / 2 - track.clientWidth / 2;
    track.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
  };

  scrollNext = (event) => {
    event?.preventDefault();
    const realCount = this.#realSlides.length;
    if (realCount <= 1) return;

    const { track } = this.refs;
    const allSlides = this.#allSlides;
    const currentDomCenter = this.#realOffset + this.#activeIndex;

    // Scroll to the next DOM slide (could be a clone)
    const nextDomIndex = currentDomCenter + 1;
    if (nextDomIndex >= allSlides.length) return;

    const slide = allSlides[nextDomIndex];
    const targetScrollLeft = slide.offsetLeft + slide.offsetWidth / 2 - track.clientWidth / 2;
    track.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
  };
}

customElements.define('shoppable-carousel-component', ShoppableCarouselComponent);
